import { init, reactRouterV6Instrumentation } from "@sentry/react";
import { Integrations } from "@sentry/tracing";
import type { Integration } from "@sentry/types";
import { useEffect } from "react";

import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from "@shared/react-router-dom";
import { isProduction, isProductionBundle, isSandbox } from "@shared/utils/fullscriptEnv";

import { beforeSend } from "./beforeSend";

interface Props {
  enableRouterInstrumentation?: boolean;
  tracesSampleRate?: number;
}

const getIntegrations = (enableRouterInstrumentation: boolean): Integration[] => {
  // Don't enable transactions in staging, sandbox, development or test
  if (!isProduction()) return [];

  // Allows checkout, oauth, unsubscribe etc to work
  // Required to support SPAs that don't use ReactRouter
  if (!enableRouterInstrumentation) {
    return [new Integrations.BrowserTracing()];
  }

  return [
    new Integrations.BrowserTracing({
      routingInstrumentation: reactRouterV6Instrumentation(
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes
      ),
    }),
  ];
};

const environment = () => {
  if (isProduction()) {
    return "production";
  } else if (isSandbox()) {
    return "sandbox";
  } else if (process.env.FORCE_ENABLE_SENTRY === "true") {
    return "development";
  } else {
    return "staging";
  }
};

const initSentry = ({ enableRouterInstrumentation, tracesSampleRate }: Props = {}) => {
  init({
    dsn: "https://b1e6050ac9de430b807ad68a57ea5816@o927579.ingest.sentry.io/5878763",
    integrations: getIntegrations(enableRouterInstrumentation),
    tracesSampleRate: tracesSampleRate || 1.0,
    enabled: process.env.FORCE_ENABLE_SENTRY === "true" || isProductionBundle(),
    environment: environment(),
    release: process.env.CI_COMMIT_SHA,
    denyUrls: [
      // browser extensions
      /extension:\/\//gi,
      /^file:\/\//gi,
    ],
    beforeSend,
  });
};

export { initSentry };
